/* eslint-disable jsx-a11y/alt-text */
import React from 'react'

import ErrorIcon from 'assets/images/icons/exclamation.png'
import styles from 'components/pages/support/help/helpStyles'
import { HelpComponentProps } from 'types/Help'
import { useFormatMessage } from 'lib/localization'
import { isMyFeatureOn } from 'lib/splitio'
import { FEATURES } from 'lib/splitioFeatures'

const BASE_I18N_KEY = 'ess.help_regex'

function RegexI18n({ classes, productName }: { classes: ReturnType<typeof styles>; productName?: string }) {
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const specialCharsLink = (txt: React.ReactNode) => <a href="#help-regex-specialchars">{txt}</a>
  const formatValues = { productName, specialCharsLink }

  return (
    <div className={classes.content} data-testid="content">
      <p>{formatMessage('intro', formatValues)}</p>

      <div className={classes.errorNotice}>
        <img src={ErrorIcon} className={classes.noticeIcon} alt="" />
        <strong>{formatMessage('caution.title')}</strong> {formatMessage('caution.message', formatValues)}
      </div>

      <p>{formatMessage('topics')}</p>
      <ul>
        <li>
          <a href="#help-regex-operators">{formatMessage('topics_list.operators')}</a>
        </li>
        <li>
          <a href="#help-regex-classes">{formatMessage('topics_list.classes')}</a>
        </li>
        <li>
          <a href="#help-regex-misc">{formatMessage('topics_list.misc')}</a>
        </li>
        <li>
          <a href="#help-regex-specialchars">{formatMessage('topics_list.special_chars')}</a>
        </li>
        <li>
          <a href="#help-regex-examples">{formatMessage('topics_list.examples')}</a>
        </li>
      </ul>

      <h3 id="help-regex-operators">{formatMessage('operators.title')}</h3>
      <p>{formatMessage('operators.intro', formatValues)}</p>
      <ul className={classes.noListStyle}>
        <li>{formatMessage('operators.items.asterisk')}</li>
        <li>{formatMessage('operators.items.plus')}</li>
        <li>{formatMessage('operators.items.question')}</li>
        <li>{formatMessage('operators.items.pipe')}</li>
        <li>{formatMessage('operators.items.parentheses')}</li>
      </ul>

      <h3 id="help-regex-classes">{formatMessage('classes.title')}</h3>
      <p>{formatMessage('classes.intro')}</p>
      <ul className={classes.noListStyle}>
        <li>{formatMessage('classes.items.dot')}</li>
        <li>{formatMessage('classes.items.ac')}</li>
        <li>{formatMessage('classes.items.not_ac')}</li>
        <li>{formatMessage('classes.items.a_to_z')}</li>
        <li>{formatMessage('classes.items.a_to_z_dot')}</li>
        <li>{formatMessage('classes.items.a_to_z_dash')}</li>
        <li>{formatMessage('classes.items.digit')}</li>
        <li>{formatMessage('classes.items.not_digit')}</li>
        <li>{formatMessage('classes.items.word')}</li>
        <li>{formatMessage('classes.items.not_word')}</li>
        <li>{formatMessage('classes.items.space')}</li>
        <li>{formatMessage('classes.items.not_space')}</li>
      </ul>

      <h3 id="help-regex-misc">{formatMessage('misc.title')}</h3>
      <ul className={classes.noListStyle}>
        <li>{formatMessage('misc.items.caret')}</li>
        <li>{formatMessage('misc.items.dollar')}</li>
        <li>{formatMessage('misc.items.boundary')}</li>
        <li>{formatMessage('misc.items.tab')}</li>
      </ul>

      <h3 id="help-regex-specialchars">{formatMessage('special_chars.title')}</h3>
      <p>{formatMessage('special_chars.intro', formatValues)}</p>
      <div className={classes.quote}>{formatMessage('special_chars.chars')}</div>

      <h3 id="help-regex-examples">{formatMessage('examples.title')}</h3>
      <ul className={classes.noListStyle}>
        <li>{formatMessage('examples.items.cialis')}</li>
        <li>{formatMessage('examples.items.pharmacy')}</li>
        <li>{formatMessage('examples.items.viagra')}</li>
        <li>{formatMessage('examples.items.digits')}</li>
        <li>{formatMessage('examples.items.bad_good')}</li>
        <li>{formatMessage('examples.items.free')}</li>
        <li>{formatMessage('examples.items.v_i1_agra')}</li>
        <li>{formatMessage('examples.items.v_ia_1a_gra')}</li>
        <li>{formatMessage('examples.items.v_pipe_agra')}</li>
        <li>{formatMessage('examples.items.v_optional_agra')}</li>
        <li>{formatMessage('examples.items.free_asterisk')}</li>
        <li>{formatMessage('examples.items.free_viagra')}</li>
      </ul>
    </div>
  )
}

function Regex(props: HelpComponentProps) {
  const classes = styles()
  let isSmartlingEnabled = false
  try {
    isSmartlingEnabled = isMyFeatureOn(FEATURES.EGD_React_Smartling_Translations)
  } catch {
    // Default to OFF when called outside Redux context (e.g., renderToString for search)
  }

  // Flag ON: Use translated content
  if (isSmartlingEnabled) {
    return <RegexI18n classes={classes} productName={props.productName} />
  }

  // Flag OFF: Original hardcoded English content
  return (
    <div className={classes.content} data-testid="content">
      <p>
        {props.productName} allows, but does not require, the use of regular expressions in many of its block/allow
        features. A regular expression is a combination of characters or character classes and operators which allow
        text to be described for matching purposes.
        <b>Matches are NOT case-sensitive.</b> All regular expressions are lower case before use as is the text against
        which expressions are being matched. For this reason, none of the capital letter character classes can be used.
      </p>

      <div className={classes.errorNotice}>
        <img src={ErrorIcon} className={classes.noticeIcon} />
        <strong>Caution:</strong> Be careful when using <a href="#specialcharacters">special characters</a> such as |,
        *, &apos;.&apos; as they are not taken literally unless escaped with a preceding &apos;\&apos; character.
      </div>

      <p>Topics in this page include:</p>
      <ul>
        <li>
          <a href="#help-regex-operators">Operators</a>
        </li>
        <li>
          <a href="#help-regex-classes">Character Classes</a>
        </li>
        <li>
          <a href="#help-regex-misc">Miscellaneous characters</a>
        </li>
        <li>
          <a href="#help-regex-specialchars">Special characters</a>
        </li>
        <li>
          <a href="#help-regex-examples">Examples</a>
        </li>
      </ul>

      <h3 id="help-regex-operators">Operators</h3>
      <p>Operators recognized by {props.productName} in regular expressions include:</p>
      <ul className={classes.noListStyle}>
        <li>
          <b>*</b> - zero or more times
        </li>
        <li>
          <b>+</b> - one or more times
        </li>
        <li>
          <b>?</b> - zero or one time
        </li>
        <li>
          <b>|</b> - or
        </li>
        <li>
          <b>( )</b> - grouping
        </li>
      </ul>

      <h3 id="help-regex-classes">Character Classes</h3>
      <p>
        A character class matches a character or range of characters that is listed between brackets &apos;[&apos; and
        &apos;]&apos;. A dash (&apos;-&apos;) is used to specify a range of characters. Use the &apos;^&apos; character
        inside the &apos;[&apos; and &apos;]&apos; to negate a class. Some examples are:
      </p>

      <ul className={classes.noListStyle}>
        <li>
          <b>.</b> - any character except newline
        </li>
        <li>
          <b>[ac]</b> - one of the following characters: a A c C
        </li>
        <li>
          <b>[^ac]</b> - anything but the characters &apos;a&apos;, &apos;A&apos;, &apos;c&apos;, &apos;C&apos;
        </li>
        <li>
          <b>[a-z]</b> - letters &apos;a&apos; through &apos;z&apos; regardless of case
        </li>
        <li>
          <b>[a-z.]</b> - letters &apos;a&apos; through &apos;z&apos; or &apos;A&apos; through &apos;Z&apos; or a dot{' '}
        </li>
        <li>
          <b>[a-z\-]</b> - any alphabetic character or a dash
        </li>
        <li>
          <b>\d</b> - digit, shortcut for <b>[0-9]</b>
        </li>
        <li>
          <b>[^\d]</b> - non-digit
        </li>
        <li>
          <b>\w</b> - part of word: shortcut for <b>[A-Za-z0-9_]</b>
        </li>
        <li>
          <b>[^\w]</b> - non-word character
        </li>
        <li>
          <b>\s</b> - space character: shortcut for <b>[ \n\r\t]</b>
        </li>
        <li>
          <b>[^\s]</b> - non-space character
        </li>
      </ul>

      <h3 id="help-regex-misc">Miscellaneous characters</h3>
      <ul className={classes.noListStyle}>
        <li>
          <b>^</b> - beginning of line
        </li>
        <li>
          <b>$</b> - end of line
        </li>
        <li>
          <b>\b</b> - word boundary
        </li>
        <li>
          <b>\t</b> - tab character
        </li>
      </ul>

      <h3 id="help-regex-specialchars">Special characters</h3>
      <p>
        The following characters have a special meaning in regular expressions in {props.productName} and are prepended
        by a <b>\</b> character when they are interpreted literally:
      </p>
      <div className={classes.quote}>. [ ] \ * ? $ ( ) | ^ @</div>

      <h3 id="help-regex-examples">Examples</h3>
      <ul className={classes.noListStyle}>
        <li>
          <b>\bcialis\b</b> - matches cialis, but not specialist
        </li>
        <li>
          <b>p[^\w]?h[^\w]?a[^\w]?r[^\w]?m[^\w]?a[^\w]?c[^\w]?y</b> - matches pha,rmacy or P.harm-acy
        </li>
        <li>
          <b>viagra</b> - matches viagra, VIAGRA or vIaGRa
        </li>
        <li>
          <b>\d+</b> - matches 1 or more digits: 0, 42, 007
        </li>
        <li>
          <b>(bad|good)</b> - matches the letters &apos;bad&apos; or matches the letters &apos;good&apos;
        </li>
        <li>
          <b>^free</b> - matches the letters &apos;free&apos; at the beginning of a line
        </li>
        <li>
          <b>v[i1]agra</b> - matches viagra or v1agra
        </li>
        <li>
          <b>v(ia|1a)gra</b> - matches viagRA or v1agra
        </li>
        <li>
          <b>v\|agra</b> - matches v|Agra, V|Agra, v|agRa, etc.
        </li>
        <li>
          <b>v(i|1|\|)?agra</b> - matches vagra, viaGra, v1agrA, V|AGRA, etc.
        </li>
        <li>
          <b>\*FREE\*</b> - matches *FREE*, *frEE*, etc.
        </li>
        <li>
          <b>\*FREE\* V.*GRA</b> - matches *FREE* VIAGRA, *Free* VEHiCLegrA, etc.
        </li>
      </ul>
    </div>
  )
}

export default Regex
/* eslint-enable jsx-a11y/alt-text */
